<script>
import { tick } from "svelte";
import {
	eventGroupState,
	isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
	editorState,
	saveEditorSettings,
	clampTableTextEditorWidth,
	clampTableTextEditorHeight,
	MIN_TABLE_TEXT_EDITOR_WIDTH,
	MIN_TABLE_TEXT_EDITOR_HEIGHT,
} from "@app/store/editorStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import debounce from "just-debounce-it";
import * as m from "@app/paraglide/messages.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import QuillEditor from "../events/QuillEditor.svelte";

/** @type {{ row: any, column: any, api?: any }} */
let { row, column } = $props();
let textProperty = $derived(
	column?.textProperty || (column?.id === "original" ? "original" : "text"),
);

let editing = $state(false);
/** Ignore editorState.editing=false briefly after open (dblclick / clickoutside race). */
let editGuardUntil = 0;
/** @type {HTMLDivElement | undefined} */
let cellEl = $state();
/** @type {HTMLDivElement | undefined} */
let popupEl = $state();
let popupWidth = $state(editorState.tableTextEditorWidth);
let popupHeight = $state(editorState.tableTextEditorHeight);
let anchorRight = $state(false);

let eventIndex = $derived(row?.originalIndex);
let event = $derived(
	eventGroupState[projectState.selected]?.events?.[eventIndex],
);
let isEditable = $derived(column?.editable !== false);
let plainText = $derived(convertToPlainText(event?.[textProperty] || ""));
let displayHtml = $derived(event?.[textProperty] || "");
let isExpanded = $derived(
	eventGroupState[projectState.selected]?.selected?.[0] === eventIndex,
);
let editorAriaLabel = $derived(
	textProperty === "original"
		? m.editor_table_column_original()
		: m.editor_table_column_text(),
);

function positionPopup() {
	if (!cellEl) {
		return;
	}

	const cellRect = cellEl.getBoundingClientRect();
	const padding = 8;
	let width = clampTableTextEditorWidth(editorState.tableTextEditorWidth);
	let height = clampTableTextEditorHeight(editorState.tableTextEditorHeight);
	const spaceRight = window.innerWidth - cellRect.left - padding;
	const spaceLeft = cellRect.right - padding;

	if (spaceRight >= width || spaceRight >= spaceLeft) {
		anchorRight = false;
		width = Math.min(width, Math.max(spaceRight, cellRect.width));
	} else {
		anchorRight = true;
		width = Math.min(width, Math.max(spaceLeft, cellRect.width));
	}

	const spaceBelow = window.innerHeight - cellRect.top - padding;
	height = Math.min(
		height,
		Math.max(MIN_TABLE_TEXT_EDITOR_HEIGHT, spaceBelow),
	);

	popupWidth = Math.max(MIN_TABLE_TEXT_EDITOR_WIDTH, Math.round(width));
	popupHeight = Math.max(MIN_TABLE_TEXT_EDITOR_HEIGHT, Math.round(height));
}

/**
 * @param {MouseEvent | KeyboardEvent} e
 */
function startEditing(e) {
	e.stopPropagation();
	e.preventDefault();

	if (!isEditable) {
		return;
	}

	if (isSelectedEventGroupLocked()) {
		toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
		return;
	}

	const egIdx = projectState.selected;
	const idx = row?.originalIndex;
	if (idx == null || egIdx == null) return;

	const liveEvent = eventGroupState[egIdx]?.events?.[idx];
	if (!liveEvent) return;

	eventGroupState[egIdx].selected = [idx];
	editorState.editing = true;
	editGuardUntil = Date.now() + 250;
	popupWidth = clampTableTextEditorWidth(editorState.tableTextEditorWidth);
	popupHeight = clampTableTextEditorHeight(editorState.tableTextEditorHeight);
	anchorRight = false;
	// Defer mount so the opening dblclick doesn't hit Quill clickoutside
	setTimeout(async () => {
		editing = true;
		await tick();
		positionPopup();
	}, 0);
}

function stopEditing() {
	editing = false;
	if (editorState.editing) {
		editorState.editing = false;
	}
}

/**
 * @param {KeyboardEvent} e
 */
function onWindowKeydown(e) {
	if (!editing) return;
	if (e.key === "Escape") {
		e.stopPropagation();
		stopEditing();
	}
}

$effect(() => {
	if (!editing) return;

	const selected0 = eventGroupState[projectState.selected]?.selected?.[0];
	const stillSelected = selected0 === row?.originalIndex;
	const stillEditingFlag = editorState.editing;
	const locked = isSelectedEventGroupLocked();

	if (!stillSelected || locked) {
		editing = false;
		return;
	}

	if (!stillEditingFlag && Date.now() > editGuardUntil) {
		editing = false;
	}
});

$effect(() => {
	if (!editing || !popupEl) {
		return;
	}

	let skipInitial = true;
	let active = true;
	const persistSize = debounce(() => {
		if (!active || !popupEl) {
			return;
		}

		const width = clampTableTextEditorWidth(popupEl.offsetWidth);
		const height = clampTableTextEditorHeight(popupEl.offsetHeight);
		editorState.tableTextEditorWidth = width;
		editorState.tableTextEditorHeight = height;
		saveEditorSettings();
	}, 150);

	const observer = new ResizeObserver(() => {
		if (!active || skipInitial) {
			skipInitial = false;
			return;
		}

		persistSize();
	});
	observer.observe(popupEl);

	return () => {
		active = false;
		observer.disconnect();
	};
});
</script>

<svelte:window onkeydown={onWindowKeydown} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	bind:this={cellEl}
	class="quill-text-cell"
	class:is-editing={editing}
	ondblclick={!editing && isEditable ? startEditing : undefined}
>
	{#if editing}
		<div
			bind:this={popupEl}
			class="quill-text-edit"
			class:anchor-right={anchorRight}
			role="presentation"
			data-action="ignore-click"
			style:width="{popupWidth}px"
			style:height="{popupHeight}px"
			onclick={(e) => e.stopPropagation()}
			onmousedown={(e) => e.stopPropagation()}
			onpointerdown={(e) => e.stopPropagation()}
		>
			{#if event}
				<QuillEditor bind:event bind:eventIndex {textProperty} />
			{/if}
		</div>
	{:else}
		<div
			class="quill-text-idle"
			class:is-expanded={isExpanded}
			role="textbox"
			tabindex="0"
			aria-readonly="true"
			aria-label={editorAriaLabel}
			title={plainText}
			onkeydown={(e) => {
				if (!isEditable) return;
				if (e.key === "Enter" || e.key === "F2") {
					startEditing(e);
				}
			}}
		>
			{#if plainText}
				{@html displayHtml}
			{:else}
				&nbsp;
			{/if}
		</div>
	{/if}
</div>

<style>
.quill-text-idle {
	display: block;
	flex: 1;
	align-self: stretch;
	min-width: 0;
	min-height: 100%;
	width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	user-select: text;
	cursor: text;
	line-height: 1.3;
}
.quill-text-idle :global {
	p {
		display: inline;
		margin: 0;
	}
	p + p::before {
		content: " ";
	}
	br {
		display: none;
	}
}
.quill-text-idle.is-expanded {
	overflow: visible;
	text-overflow: clip;
	overflow-wrap: anywhere;
	white-space: pre-wrap;
}
.quill-text-idle.is-expanded :global {
	p {
		display: block;
	}
	p + p::before {
		content: none;
	}
	br {
		display: unset;
	}
}
.quill-text-cell {
	position: relative;
	display: flex;
	align-items: flex-start;
	width: 100%;
	height: 100%;
	min-height: 0;
	cursor: text;
}
.quill-text-cell.is-editing {
	cursor: default;
	align-items: center;
}
.quill-text-edit {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 30;
	min-width: 100%;
	min-height: 160px;
	overflow: auto;
	resize: both;
	background: var(--wx-background, #1e1e1e);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
	border-radius: 0.25rem;
	padding: 2px;
	box-sizing: border-box;
}
.quill-text-edit.anchor-right {
	left: auto;
	right: 0;
}
.quill-text-edit :global(.QuillEditor) {
	height: 100%;
	min-height: 100%;
}
</style>
